import { oc } from "@orpc/contract";
import { z } from "zod";
import { EMAIL_IMPORTANCE_VALUES } from "@erp/shared-types";

// ── Shared Schemas ────────────────────────────────────────────────────

export const EmailDraftStatusSchema = z.enum(["draft", "sent", "discarded"]);

/** Priority flag for the outgoing message ("low" | "normal" | "high"). */
export const EmailImportanceSchema = z.enum(EMAIL_IMPORTANCE_VALUES);

/** Summary returned inline with suggested actions (lightweight). */
export const EmailDraftSummarySchema = z.object({
  id: z.uuid(),
  to: z.string(),
  cc: z.string().nullable(),
  subject: z.string(),
  body: z.string(),
  status: EmailDraftStatusSchema,
  isAiGenerated: z.boolean(),
  createdAt: z.string(),
});

/** Full draft response including HTML content for the editor. */
export const EmailDraftResponseSchema = z.object({
  id: z.uuid(),
  to: z.string(),
  cc: z.string().nullable(),
  bcc: z.string().nullable(),
  subject: z.string(),
  body: z.string(),
  htmlContent: z.string().nullable(),
  sendingMailbox: z.string(),
  status: EmailDraftStatusSchema,
  isAiGenerated: z.boolean(),
  /** Priority flag for the outgoing message. */
  importance: EmailImportanceSchema,
  /** Document UUIDs the clerk selected/uploaded to attach when sending. */
  documentAttachmentIds: z.array(z.uuid()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// ── Contract ──────────────────────────────────────────────────────────

export const emailDraftsContracts = {
  /** Fetch a single email draft by ID. */
  get: oc
    .input(
      z.object({
        draftId: z.uuid(),
      }),
    )
    .output(
      z.object({
        draft: EmailDraftResponseSchema,
      }),
    )
    .route({
      method: "GET",
      summary: "Get an email draft",
      description:
        "Returns one email draft in the org: recipients, subject, HTML body, sending mailbox, importance, attached document ids, status and whether AI wrote it.",
    }),

  /** Update draft fields (save edits before sending). */
  update: oc
    .input(
      z.object({
        draftId: z.uuid(),
        to: z.string().optional(),
        cc: z.string().optional(),
        bcc: z.string().optional(),
        subject: z.string().optional(),
        htmlContent: z.string().optional(),
        /**
         * Reroute the draft to a different Graph mailbox before sending.
         * Server-side authz requires the user to either own this mailbox
         * UPN or for the UPN to match an organization-level outbox config.
         */
        sendingMailbox: z.string().email().optional(),
        /**
         * Document UUIDs to attach to the outbound email. Replaces (not
         * appends to) the current selection. Each id must belong to a
         * Document the requester's org can read.
         */
        documentAttachmentIds: z.array(z.uuid()).optional(),
        /** Mark the outgoing message's priority before sending. */
        importance: EmailImportanceSchema.optional(),
      }),
    )
    .output(
      z.object({
        draft: EmailDraftResponseSchema,
      }),
    )
    .route({
      method: "POST",
      summary: "Edit an email draft",
      description:
        "Saves edits to an email draft before sending: recipients, subject, HTML body, importance, attached documents (replaces the selection) or a different authorized sending mailbox.",
    }),

  /** Send a draft via Microsoft Graph. */
  send: oc
    .input(
      z.object({
        draftId: z.uuid(),
      }),
    )
    .output(
      z.object({
        success: z.boolean(),
        message: z.string(),
      }),
    )
    .route({
      method: "POST",
      summary: "Send an email draft",
      description:
        "Sends a saved email draft from its sending mailbox to the recipients, with its attached documents, and marks the draft sent.",
    }),
} as const;

// ── Type Exports ──────────────────────────────────────────────────────

export type EmailDraftSummary = z.infer<typeof EmailDraftSummarySchema>;
export type EmailDraftResponse = z.infer<typeof EmailDraftResponseSchema>;
export type EmailDraftStatus = z.infer<typeof EmailDraftStatusSchema>;
export type EmailImportance = z.infer<typeof EmailImportanceSchema>;
